import { saturate } from "./functions";
import { PlaneRange } from "./plane-range";
import type { ReadonlyVec } from "./readonly";
import { Vec } from "./vec";
import type { VecLike } from "./vec-like";
import { Vec2 } from "./vec2";

export class LineReadable<Point extends ReadonlyVec>{
	public a:Point;
	public b:Point;

	public constructor(
		a:Point,
		b:Point,
	){
		this.a=a;
		this.b=b;
	}

	public *[Symbol.iterator](){
		yield this.a;
		yield this.b;
	}

	public equals(that:LineReadable<any>){
		return this.a.eq(that.a) && this.b.eq(that.b);
	}

	public count(){
		return 2;
	}

	public at(i:number){
		if(i<0)
			i+=2;
		if(i===0)
			return this.a;
		if(i===1)
			return this.b;
		return undefined;
	}

	public atIndex(i:number):Point['cloneType']{
		if(i<=0)
			return this.a.clone();
		if(i>=1)
			return this.b.clone();
		return this.a.clone().lerp(this.b,i);
	}

	public _atIndex<T extends Vec>(i:number, out:T):T{
		if(i<=0)
			return out.copy(this.a);
		if(i>=1)
			return out.copy(this.b);
		return out.blend(this.a,this.b,i);
	}

	public atDist(dist:number):Point['cloneType']{
		if(dist<=0)
			return this.a.clone();
		const lenSq=this.lenSq();
		if(dist*dist>=lenSq)
			return this.b.clone();
		return this.a.clone().lerp(this.b,dist/Math.sqrt(lenSq));
	}

	public _atDist<T extends Vec>(dist:number, out:T):T{
		if(dist<=0)
			return out.copy(this.a);
		const lenSq=this.lenSq();
		if(dist*dist>=lenSq)
			return out.copy(this.b);
		return out.blend(this.a,this.b,dist/Math.sqrt(lenSq));
	}

	public len(){
		return this.a.distTo(this.b);
	}

	public lenSq(){
		return this.a.distToSq(this.b);
	}

	public delta(){
		return this.b.delta(this.a);
	}

	public _delta<T extends Vec>(out:T):T{
		return out.diff(this.b,this.a);
	}

	public dir(){
		return this.delta().nrm();
	}

	public _dir<T extends Point["vecType"]>(out:T):T{
		return this._delta(out).nrm();
	}

	public towardsA(){
		return this.dir();
	}

	public _towardsA<T extends Point["vecType"]>(out:T):T{
		return this._dir(out);
	}

	public towardsB(){
		return this.dir().neg();
	}

	public _towardsB<T extends Point["vecType"]>(out:T):T{
		return this._dir(out).neg();
	}

	public azimuth(){
		return Vec.azimuth({
			x: this.b.x-this.a.x,
			y: this.b.y-this.a.y,
		});
	}

	public planeRange(){
		const nrm=this.dir();
		return new PlaneRange(nrm,nrm.dot(this.a),nrm.dot(this.b));
	}

	public distOf(p:VecLike){
		const v=this._dir(this.a.tmpA);
		return v.dot(p)-v.dot(this.a);
	}

	public indexOf(p:VecLike){
		const v=this._delta(this.a.tmpA);
		if(v.z!==undefined && p.z===undefined)
			v.z=0;
		const dotA=v.dot(this.a);
		const dotB=v.dot(this.b);
		if(dotA===dotB)
			return 0;
		return (v.dot(p)-dotA)/(dotB-dotA);
	}

	public distToPointSq(p:VecLike){
		let index=this.indexOf(p);
		index=Math.min(index,1);
		index=Math.max(index,0);
		return this._atIndex(index,this.a.tmpA).distToSq(p);
	}

	public distToPoint(p:VecLike){
		return Math.sqrt(this.distToPointSq(p));
	}

	public closestPointToPoint<Out extends Vec>(p:VecLike, out:Out):Out{
		return this._atIndex(saturate(this.indexOf(p)),out);
	}

	public closestPoint(focus:VecLike):Point['cloneType']{
		return this.atIndex(saturate(this.indexOf(focus)));
	}

	public _closestPoint<Out extends Vec>(focus:VecLike, out:Out){
		return this.closestPointToPoint(focus,out);
	}

	// public intersectionsWithCircleLike(
	// 	circle:{center:Vec;radius:number},
	// 	inclusiveA:boolean,
	// 	inclusiveB:boolean,
	// 	inclusiveCircle:boolean,
	// ){
	// 	const cc=circle.center;
	// 	const cr=circle.radius;

	// 	const i=this.indexOf(cc);
	// 	const c=this.atIndex(i);
	// 	const ec=c.distTo(cc);

	// 	const intersections:LineReadable.Intersection[]=[];
	// 	if(ec<cr){
	// 		const ab=this.len()
	// 		const t=i*ab;
	// 		// line intersects the circle
	// 		// distance from t to intersection
	// 		const dt=Math.sqrt(cr*cr-ec*ec);

	// 		function isValid(t:number){
	// 			if(inclusiveA){
	// 				if(t<0)
	// 					return false;
	// 			}else{
	// 				if(t<=0)
	// 					return false;
	// 			}
	// 			if(inclusiveB){
	// 				if(ab<t)
	// 					return false;
	// 			}else{
	// 				if(ab<=t)
	// 					return false;
	// 			}
	// 			return true;
	// 		}

	// 		for(const ti of [t-dt,t+dt]){
	// 			if(isValid(ti)){
	// 				const p=new LineReadable.Intersection();
	// 				p.indexA=ti/ab;
	// 				p.distA=p.indexA*ab;
	// 				intersections.push(this._atIndex(p.indexA,p));
	// 			}
	// 		}
	// 	}else if(ec===cr && inclusiveCircle){
	// 		// line is tangent, e is the intersection
	// 		const p=new LineReadable.Intersection(c.x,c.y);
	// 		p.indexA=this.indexOf(c);
	// 		p.distA=p.indexA*this.len();
	// 		intersections.push(this._atIndex(p.indexA,p));
	// 	}

	// 	return intersections;
	// }
}

export namespace LineReadable{
	export class Intersection extends Vec2{
		distA:number;
		distB:number;
		indexA:number;
		indexB:number;
	}
}
